<template>
  <div class="App">
    <LoadingOverlay
      v-show="showOverlay"
      :message="overlayMessage"
      :show-spinner="showOverlaySpinner"
      :show-reload-immediately="showReloadImmediately"
    />
    <RouterView v-show="!showOverlay" />
  </div>
</template>

<script setup lang="ts">
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import { EnvProvider, Envs } from '@/utils/EnvProvider';
import { addGtag, event, customMap } from 'vue-gtag';
import { CONFIG_ROUTES } from '@/config/routes';
import { useAuthStore } from '@/store/auth';
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const auth = useAuthStore();
const route = useRoute();

const hasUnresolvedRoute = computed(() => route.matched.length === 0);
const showReloadImmediately = computed(() => hasUnresolvedRoute.value);

const showOverlay = computed(() => {
  /**
   * partial auth pages may have an authenticated user, but not
   * fully authenticated for certain gql operations, we need
   * those pages to render without a user object
   */
  const isPartialAuth = route?.meta?.partialAuth;

  // app mounted w/ route middleware, no route defined yet, and awaiting authentication check
  const isAppInit = (!route.name && auth.account.isAuthenticating)

  const isNotSwitchingProfiles = !auth.account.isSwitchingProfiles;

  // auth routes
  const isAuthRoute = route.name?.startsWith?.('auth.'); // && !isTermsPage && !isVerifyPage;
  // ... while awaiting authentication check
  const isAuthRouteLoading = (isAuthRoute && auth.account.isAuthenticating)
  // ... while authenticated
  const isAuthRouteAuthenticated = (isAuthRoute && auth.account.isAuthenticated)

  // protected routes while auth setup not completed
  const isProtectedRoute = route.fullPath?.startsWith?.(CONFIG_ROUTES.APP_PATH_PREFIX+'/');
  const isProtectedRouteWithoutAuth = (isProtectedRoute && !auth.isReady);

  const isAuthLoading =
    isAppInit ||
    (isNotSwitchingProfiles && (isAuthRouteLoading || isAuthRouteAuthenticated)) ||
    isProtectedRouteWithoutAuth;

  // Do not show overlay if we are on a partial auth page
  return hasUnresolvedRoute.value || (isAuthLoading && !(isPartialAuth));
});

const showOverlaySpinner = computed(() => !showReloadImmediately.value);

const overlayMessage = computed(() => {
  if (showReloadImmediately.value) {
    return '';
  }
  if (auth.loadingMessage) {
    return auth.loadingMessage;
  }
  return '';
});

onMounted(() => {
  if (EnvProvider.env !== Envs.Cloud) return;

  // Load Google Analytics
  if (import.meta.env.PROD) {
    addGtag().catch((error) => {
      console.error('Failed to load analytics', {error});
    });
  }
})
</script>

<style lang="scss" scoped>
.App {
  height: 100%;
  width: 100%;
  box-shadow: none;
  position: relative;
  z-index: 5;
}
</style>
